import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gosafeApi } from "../../api";
import {
  onLoadingDoctors,
  onLoading,
  onLoaded,
  onLoadDoctors,
  onFailedDoctors,
  onErrorMessage,
  onSuccessMessage,
  changePageSize,
  changePage,
  onUpdateDoctor,
} from "../../store/secretary/doctors";
import { translations, replaceEmptyToNull } from "../../helpers";

export const useDoctorsStore = () => {
  const dispatch = useDispatch();

  const {
    isLoadingData,
    isLoading,
    doctors,
    message,
    pageSize,
    page,
    totalPages,
  } = useSelector((state) => state.doctors);

  const startGetDoctors = useCallback(
    async (page, pageSize) => {
      try {
        dispatch(onLoadingDoctors());

        const { data } = await gosafeApi.get("doctor", {
          params: { page, pageSize },
        });

        dispatch(onLoadDoctors(data));
      } catch (error) {
        dispatch(
          onFailedDoctors(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startChangePageSize = useCallback(
    async (pageSize) => {
      dispatch(changePageSize(pageSize));
      await startGetDoctors(page, pageSize);
    },
    [dispatch, startGetDoctors, page]
  );

  const startChangePage = useCallback(
    async (page) => {
      dispatch(changePage(page));
      await startGetDoctors(page, pageSize);
    },
    [dispatch, startGetDoctors, pageSize]
  );

  const startAddPage = useCallback(async () => {
    if (totalPages > parseInt(page)) {
      const newPage = (parseInt(page) + 1).toString();
      dispatch(changePage(newPage));
      await startGetDoctors(newPage, pageSize);
    }
  }, [dispatch, startGetDoctors, page, pageSize, totalPages]);

  const startRemovePage = useCallback(async () => {
    if (parseInt(page) > 1) {
      const newPage = (parseInt(page) - 1).toString();
      dispatch(changePage(newPage));
      await startGetDoctors(newPage, pageSize);
    }
  }, [dispatch, startGetDoctors, page, pageSize, totalPages]);

  const reloadDoctors = useCallback(async () => {
    await startGetDoctors(page, pageSize);
  }, [startGetDoctors, page, pageSize]);

  const startUpdateDoctor = useCallback(
    async (doctor, headquartersId) => {
      try {
        dispatch(onLoading());
        const { id, ...newDoctor } = {
          ...doctor,
          headquarters: headquartersId,
        };

        // console.log("id", id);
        // console.log("newDoctor", newDoctor);

        const { data } = await gosafeApi.put(`doctor/${id}`, newDoctor);

        dispatch(onUpdateDoctor(data.doctor));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startRegisterDoctor = useCallback(
    async (body, headquartersId) => {
      try {
        dispatch(onLoading());

        const newDoctor = {
          ...body,
          headquarters: headquartersId,
        };

        // console.log("newDoctor", newDoctor);

        const { data } = await gosafeApi.post("doctor", newDoctor);

        dispatch(onSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  return {
    // Props
    isLoadingData,
    isLoading,
    doctors,
    message,
    pageSize,
    page,
    totalPages,

    // Methods
    startGetDoctors,
    startChangePageSize,
    startChangePage,
    startAddPage,
    startRemovePage,
    reloadDoctors,
    startUpdateDoctor,
    startRegisterDoctor,
  };
};
